import { z } from "zod";
import { AuditLogDiff } from "@common/types/auditLog";
import { HistoryField } from "@common/constants/history-source-types";
import { formatInvoiceMoney } from "@common/helpers/nativeInvoice";
import { formatAuditLogRefValue } from "../../utils/historyDetailsHelpers";

export type CatalogItemRefValue = AuditLogDiff["before"];

export type ClassLabelResolver = (classId: string) => string | undefined;

const EMPTY_TEXT = "-";
const LIST_SEPARATOR = ", ";
const ACTIVE_TEXT = "Active";
const ARCHIVED_TEXT = "Archived";
const DESCRIPTION_SEPARATOR = " – ";

const classAssignmentsSchema = z.array(z.object({ classId: z.string() }));

const pricesSchema = z.array(
    z.object({
        unitAmount: z.number(),
        currency: z.string(),
        active: z.boolean(),
        isDefault: z.boolean(),
        description: z.string().nullish(),
    }),
);

const formatClassAssignments = (
    ref: CatalogItemRefValue,
    resolveClassLabel: ClassLabelResolver,
): string => {
    if (ref.valueLabel) {
        return ref.valueLabel;
    }
    const parsed = classAssignmentsSchema.safeParse(ref.value);
    if (!parsed.success) {
        return formatAuditLogRefValue(ref);
    }
    if (parsed.data.length === 0) {
        return EMPTY_TEXT;
    }
    return parsed.data
        .map(({ classId }) => resolveClassLabel(classId) ?? classId)
        .join(LIST_SEPARATOR);
};

const formatPrices = (ref: CatalogItemRefValue): string => {
    const parsed = pricesSchema.safeParse(ref.value);
    if (!parsed.success) {
        return formatAuditLogRefValue(ref);
    }
    if (parsed.data.length === 0) {
        return EMPTY_TEXT;
    }
    return parsed.data
        .map(({ unitAmount, currency, active, isDefault, description }) => {
            const flags = [
                isDefault ? "default" : null,
                active ? null : "inactive",
            ]
                .filter((flag) => flag !== null)
                .join(LIST_SEPARATOR);
            const amount = formatInvoiceMoney(unitAmount, currency);
            const label = description
                ? `${amount}${DESCRIPTION_SEPARATOR}${description}`
                : amount;
            return flags ? `${label} (${flags})` : label;
        })
        .join(LIST_SEPARATOR);
};

const formatActive = (ref: CatalogItemRefValue): string => {
    if (typeof ref.value !== "boolean") {
        return formatAuditLogRefValue(ref);
    }
    return ref.value ? ACTIVE_TEXT : ARCHIVED_TEXT;
};

export function formatCatalogItemValue(
    field: string,
    ref: CatalogItemRefValue,
    resolveClassLabel: ClassLabelResolver,
): string {
    switch (field) {
        case HistoryField.CATALOG_ITEM_CLASS_ASSIGNMENTS:
            return formatClassAssignments(ref, resolveClassLabel);
        case HistoryField.PRICES:
            return formatPrices(ref);
        case HistoryField.ACTIVE:
            return formatActive(ref);
        default:
            return formatAuditLogRefValue(ref);
    }
}
